import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Icon, Loader } from "semantic-ui-react";
import { formatDate } from "../../constants";
import { selectCurrentToken } from "../auth/authSlice";
import { uploadFileToS3Presigned } from "../../utils/presignedUpload";
import { Badge, InfoNote, Pill, cardStyle, fieldStyle, labelStyle, ui } from "../../components/adminUi";
import {
  useCreateSupportReplyMutation,
  useGetSupportTicketByIdQuery,
  useGetSupportTicketImageUrlQuery,
} from "./supportTicketApiSlice";
import { getSupportTicketStatusLabel, isReporterReply } from "./supportTicketUtils";
import {
  Attachment,
  CenteredLoader,
  ErrorNote,
  Page,
  PageBody,
  PageHeader,
} from "./supportTicketChrome";

/**
 * 피드백 상세, the trainee's side of a thread.
 *
 * Mirror image of the admin page: here the trainee's own messages are "sent" (right, blue)
 * and the admin's are "received". Those classes come from index.css, the same ones the
 * roleplay transcript uses — already the right language for a thread, so left alone.
 *
 * No 요청 종료: closing is the admin's action, and a trainee arriving at a closed ticket
 * gets a notice instead of a compose box.
 */
const CONTENT_MAX = "820px";

const SupportTicketDetailPage = () => {
  const { id } = useParams();
  const token = useSelector(selectCurrentToken);
  const [replyBody, setReplyBody] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const { data: ticket, isLoading, isError } = useGetSupportTicketByIdQuery(id);

  const { data: imageData, isLoading: isImageLoading } = useGetSupportTicketImageUrlQuery(
    id,
    { skip: !ticket?.image_s3_key }
  );

  const [createSupportReply, { isLoading: isReplying }] = useCreateSupportReplyMutation();

  const isClosed = ticket?.status === "closed";
  const busy = isReplying || isUploading;

  const handleReplySubmit = async () => {
    if (!replyBody.trim()) {
      setError("답변 내용을 입력해 주세요.");
      return;
    }

    setError(null);
    let imageS3Key = null;
    if (imageFile) {
      setIsUploading(true);
      const uploadResult = await uploadFileToS3Presigned(imageFile, token);
      setIsUploading(false);
      if (!uploadResult.success) {
        setError(uploadResult.error || "이미지 업로드에 실패했습니다.");
        return;
      }
      imageS3Key = uploadResult.key;
    }

    const result = await createSupportReply({
      ticketId: id,
      body: replyBody.trim(),
      ...(imageS3Key ? { image_s3_key: imageS3Key } : {}),
    });
    if (result.error) {
      setError("답변 등록에 실패했습니다.");
      return;
    }
    setReplyBody("");
    setImageFile(null);
  };

  if (isLoading) {
    return (
      <Page>
        <CenteredLoader />
      </Page>
    );
  }

  if (isError || !ticket) {
    return (
      <Page>
        <PageBody maxWidth={CONTENT_MAX}>
          <div style={{ paddingTop: "40px" }}>
            <ErrorNote>피드백을 불러오지 못했습니다.</ErrorNote>
            <Link to="/my-feedback">
              <Pill variant="neutral">
                <Icon name="arrow left" style={{ margin: 0 }} /> 목록으로
              </Pill>
            </Link>
          </div>
        </PageBody>
      </Page>
    );
  }

  const status = getSupportTicketStatusLabel(ticket.status);
  const replies = ticket.replies || [];

  return (
    <Page>
      <PageHeader
        title="피드백 상세"
        subtitle={formatDate(ticket.created_at)}
        backTo={<Link to="/my-feedback" />}
        right={<Badge tone={status.tone}>{status.text}</Badge>}
      />

      <PageBody maxWidth={CONTENT_MAX}>
        <div style={{ ...cardStyle, padding: "16px", marginBottom: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div className="message-row sent">
              <div className="message-bubble sent">
                <p className="message-text">{ticket.body}</p>
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
                  {formatDate(ticket.created_at)}
                </div>
              </div>
            </div>

            {ticket.image_s3_key && (
              <div style={{ margin: "12px 0 12px auto", maxWidth: "70%" }}>
                {isImageLoading && <Loader active inline size="small" />}
                {imageData?.url && <Attachment url={imageData.url} />}
              </div>
            )}

            {replies.map((reply) => {
              const mine = isReporterReply(reply, ticket);
              return (
                <div key={reply.id} className={`message-row ${mine ? "sent" : "received"}`}>
                  <div className={`message-bubble ${mine ? "sent" : "received"}`}>
                    {!mine && (
                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                        {reply.author?.full_name || reply.author?.username || "관리자"}
                      </div>
                    )}
                    <p className="message-text">{reply.body}</p>
                    {reply.image_url && (
                      <Attachment url={reply.image_url} style={{ marginTop: 8 }} />
                    )}
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
                      {formatDate(reply.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}

            {replies.length === 0 && !isClosed && (
              <div
                style={{
                  fontSize: "13px",
                  color: ui.muted,
                  textAlign: "center",
                  padding: "18px 0 6px",
                }}
              >
                아직 관리자 답변이 없습니다.
              </div>
            )}
          </div>
        </div>

        {isClosed ? (
          <InfoNote style={{ marginBottom: 0 }}>
            <Icon name="check circle outline" style={{ margin: 0, color: ui.accent }} />
            <span>종료된 피드백입니다. 추가 답변을 등록할 수 없습니다.</span>
          </InfoNote>
        ) : (
          <div style={{ ...cardStyle, padding: "16px" }}>
            <label style={labelStyle}>추가 답변</label>
            <textarea
              style={{ ...fieldStyle, minHeight: "104px", resize: "vertical", lineHeight: 1.5 }}
              placeholder="추가로 전달할 내용을 입력해 주세요."
              value={replyBody}
              onChange={(event) => setReplyBody(event.target.value)}
            />

            <label style={{ ...labelStyle, marginTop: "14px" }}>이미지 첨부 (선택)</label>
            <input
              type="file"
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              onChange={(event) => {
                setImageFile(event.target.files?.[0] || null);
                setError(null);
              }}
              style={{ fontSize: "13px", color: ui.muted }}
            />
            {previewUrl && (
              <img
                src={previewUrl}
                alt="첨부 미리보기"
                style={{
                  maxWidth: "100%",
                  maxHeight: 200,
                  marginTop: 10,
                  borderRadius: 12,
                  display: "block",
                }}
              />
            )}

            {error && (
              <InfoNote style={{ background: ui.redBg, color: ui.redFg, margin: "14px 0 0" }}>
                <Icon name="exclamation triangle" style={{ margin: 0 }} />
                <span>{error}</span>
              </InfoNote>
            )}

            <div style={{ marginTop: "16px" }}>
              <Pill
                variant="primary"
                disabled={busy}
                onClick={handleReplySubmit}
                style={{ height: "40px", padding: "0 20px" }}
              >
                {isUploading ? "이미지 업로드 중…" : isReplying ? "등록 중…" : "답변 등록"}
              </Pill>
            </div>
          </div>
        )}
      </PageBody>
    </Page>
  );
};

export default SupportTicketDetailPage;
